/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo } from 'react';
import { moneyFormat } from '@helpers';

import { useDispatch, useSelector } from 'react-redux';
import { getOpscore } from '@store/users/actions';

import moment from 'moment';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  SelectMonth,
  BalanceNomenclature,
  SettlementCalendarTab,
  CalendarCardInfoDate,
  SettlementCalendarSkeleton,
  Tooltip,
  Icon,
} from 'fpay-ui';
import { useAndes, useAuth } from '@hooks';
import { useBreakpoints } from '@hooks/NewBreakpoint';
import { CalendarChannels } from '@constants/transactions';
import { REGIONS } from '@constants/regions';
import { getEnv } from '@config';

const ContentDescription = styled.div`
  margin: 16px 0;
  display: flex;
  width: 100%;
  justify-content: center;
  @media (min-width: 601px) {
    justify-content: space-between;
  }
`;

const ContainerBalance = styled.div`
  display: none;
  @media (min-width: 601px) {
    display: flex;
  }
`;

const Box = styled.div`
  border-radius: 8px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 16px 0;
  @media (min-width: 600px) {
    flex-direction: row;
    padding: 16px 24px 16px 24px;
  }
`;

export const IconInfo = styled(Icon)``;

const Text = styled.div(
  () => css`
    display: flex;
    font-weight: 500;
    line-height: 1.31;
    font-size: 12px;
    color: #444b62;
    justify-content: flex-start;
    font-weight: 500;
    @media (min-width: 600px) {
      justify-content: center;
      text-align: center;
      width: auto;
      font-size: 14px;
      padding-left: 8px;
      padding-right: 8px;
      margin-bottom: 0px;
    }
  `
);

const Mount = styled.div(
  () => css`
    display: flex;
    align-items: center;
    font-weight: 500;
    line-height: 1.31
    font-size: 16px;
    color: #444b62;
    width: 100%;
    font-weight: 400;
    min-width: 10px;
    justify-content: center;
    @media (min-width: 600px) {
      font-size: 18px;
    }
  `
);

const Sign = styled.div(
  () => css`
    display: flex;
    align-items: center;
    font-weight: 500;
    line-height: 1.31;
    font-size: 18px;
    color: '#a2a7ba';
    width: 100%;
    justify-content: flex-end;
    font-weight: 400;
  `
);

const ContainerCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContainerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  @media (min-width: 600px) {
    justify-content: center;
  }
`;

export const TextoFpay = styled.div`
  text-align: center;
  font-size: 12px;
  letter-spacing: 0px;
  color: #444b62;
  opacity: 1;
  line-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 180px;
  padding: 12px;
`;

const Row = styled.div`
  display: flex;
  padding: 10px;
  > div:first-child {
    flex: 1;
  }
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
  @media (min-width: 600px) {
    flex-direction: column;
    justify-content: center;
    align-content: center;
    border-bottom: none;
    border-right: 1px solid #eee;
    width: 50%;
    &:last-child {
      border-right: none;
    }
  }
`;

const Layout = styled.div`
  display: flex;
  @media (min-width: 768px) {
    padding-bottom: 0px;
    padding-top: 3px;
  }
  > div {
    display: flex;
    margin-left: 5px;
  }
`;

const SignContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = ({ text, mount, totals, selected, tooltipKey, symbol, position }) => (
  <Row>
    <ContainerRow>
      <Text totals={totals}>{text}</Text>
      <Layout>
        <Tooltip position={position} duration={100} width="190px">
          <IconInfo icon="tooltip-info" color="#caced8" width="16px" height="16px" />
          <TextoFpay>{tooltipKey}</TextoFpay>
        </Tooltip>
      </Layout>
    </ContainerRow>
    <ContainerRow>
      <>
        <SignContainer>
          <Sign selected={selected}>{symbol}</Sign>
        </SignContainer>
        <Mount selected={selected}>{mount}</Mount>
      </>
    </ContainerRow>
  </Row>
);

const SettlementCalendar = () => {
  const [isFirstLoaded, setIsFirstLoaded] = useState(true);
  const [detailDateSelected, setDetailDateSelected] = useState();
  const { t } = useTranslation();
  const { merchant, isAllowed } = useAuth();
  const minDate = useMemo(() => new Date(merchant.created_at), []);
  const maxDate = useMemo(() => new Date(new Date().setDate(32)), []);

  const {
    availableIncome,
    settledIncome,
    errMerchant,
    errAllBalances,
    getBalances,
    totalBalance,
    errMerchantAvailable,
    futureTransactions,
    changeChannel,
    channelSelected: tabSelected,
    currentDate,
    calendar,
    isLoadingBalance,
    fetchTransactionsToMonthAndChannel,
    changeCurrentMonth,
    futureTransactionsError,
    loadSettlement,
  } = useAndes();

  const isNewMerchant = useMemo(
    () => moment().startOf('day').diff(moment(merchant.created_at), 'days') < 3,
    [merchant]
  );

  useEffect(() => {
    getBalances();
    setIsFirstLoaded(false);
    fetchTransactionsToMonthAndChannel();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isFirstLoaded) return;
    changeChannel(tabSelected);
    setDetailDateSelected();
  }, [tabSelected]);

  useEffect(() => {
    if (isFirstLoaded) return;
    fetchTransactionsToMonthAndChannel();
    setDetailDateSelected();
  }, [calendar]);

  const totalAmountByChannel = {
    [CalendarChannels.PHYSICAL]: totalBalance?.physicalMerchant?.subTotal,
    [CalendarChannels.ECOMMERCE]: totalBalance?.ecommerce?.subTotalEcommerces,
    [CalendarChannels.FALABELLA]: totalBalance?.sellerMerchant?.subTotal,
    [CalendarChannels.TOTAL]: totalBalance?.total,
  };

  const totalAmountTab = totalAmountByChannel[CalendarChannels.FALABELLA];
  const { breakpoint } = useBreakpoints();
  const isEmptyStateCalendar = isNewMerchant && !errMerchantAvailable && totalAmountTab === 0;
  const { country } = getEnv();

  const dispatch = useDispatch();

  const userSellerId = useSelector((state) => state.login.merchant.seller_id);

  useEffect(() => {
    if (isAllowed('OPSCORE')) {
      dispatch(getOpscore(userSellerId));
    }
  }, [userSellerId]);

  return (
    <ContainerCol>
      {isLoadingBalance ? (
        <SettlementCalendarSkeleton />
      ) : (
        <>
          <Box>
            <Card
              text={t('settlementCalendar.screen.calendarTotal.falabella')}
              mount={
                errMerchantAvailable
                  ? t('settlementCalendar.screen.amountNotAvailable')
                  : moneyFormat(settledIncome)
              }
              tooltipKey={t('tooltipCards.merchantCalendarFondsOutToLiquidate')}
            />
            <Card
              text={t('settlementCalendar.screen.toAvailable')}
              mount={
                errMerchant
                  ? t('settlementCalendar.screen.amountNotAvailable')
                  : moneyFormat(availableIncome)
              }
              tooltipKey={t('tooltipCards.merchantCalendarFondsOutToLiquidate')}
            />
          </Box>
          <ContentDescription>
            <SelectMonth
              date={currentDate}
              changeDate={changeCurrentMonth}
              minDate={minDate}
              maxDate={maxDate}
              disabled={errAllBalances || isEmptyStateCalendar}
            />
            <ContainerBalance>
              <BalanceNomenclature
                textBalance={t('settlementCalendar.screen.nomenclature.settledBalance')}
                textToday={t('settlementCalendar.screen.nomenclature.settledBalanceToday')}
              />
            </ContainerBalance>
          </ContentDescription>
          <SettlementCalendarTab
            tabSelected={tabSelected}
            transactionsToShow={futureTransactions}
            errAllBalances={errAllBalances}
            currentDate={currentDate}
            calendar={calendar}
            detailDateSelected={detailDateSelected}
            onClickDate={setDetailDateSelected}
            futureTransactionsError={futureTransactionsError}
            titleError={t('settlementCalendar.screen.titleError')}
            titleTextStart={t('settlementCalendar.screen.textErrorFirst')}
            titleTextEnd={t('settlementCalendar.screen.textErrorReload')}
            loadSettlement={loadSettlement}
            isEmpty={isEmptyStateCalendar}
          />
          {['xs', 'sm', 'ss', 'smm', 'll', 'md'].includes(breakpoint) &&
            detailDateSelected?.date && (
              <CalendarCardInfoDate
                date={detailDateSelected.date}
                detailDate={{ ...detailDateSelected, isDaySelected: true }}
                region={REGIONS[country]}
              />
            )}
        </>
      )}
    </ContainerCol>
  );
};

export default SettlementCalendar;
